// Section 24.10 and 25 (M5d): steward(state) -> Decision, the default-policy player used by
// observer mode and the balance harness. Each season it answers every open prompt
// (stewardAnswer), queues builds in priority order when materials allow (steward/builds.ts),
// and plans the crew (steward/crew.ts): farmers for the plots in Spring, water, food, wood and
// winter fuel covered first, then the healer, builders, quarrier, toolmaker, teacher, caretaker
// and guard, the rest where the stock is thinnest. When something essential is at risk it
// compares a few candidate plans by projected fragility with project(state, candidate, 4)
// (steward/choose.ts). It keeps the default policies, respects the retirement age and the
// child-labour policy, and uses no randomness: the same state gives the same decision.
import { config, type Config } from '../config';
import type { Decision, GameState } from '../core/types';
import { emptyDecision } from './decision';
import { stewardAnswer } from './steward/answer';
import { planBuilds } from './steward/builds';
import { choosePlan, type Choice } from './steward/choose';
import { apprenticeships, type Plan } from './steward/crew';
import { stewardCtx } from './steward/model';

export { stewardAnswer } from './steward/answer';

export interface StewardOptions {
  /** Cap on candidate plans projected this decision (0 or 1 disables projection). */
  maxCandidates?: number;
}

export interface StewardExplanation {
  decision: Decision;
  choice: Choice;
}

/** The decision plus how it was chosen (observer mode and the balance harness show this). */
export function stewardExplained(
  state: GameState,
  cfg: Config = config,
  opts: StewardOptions = {},
): StewardExplanation {
  const decision = emptyDecision(state);
  for (const prompt of state.openPrompts)
    decision.eventChoices[prompt.id] = stewardAnswer(state, prompt, cfg);
  const empty: Choice = {
    plan: { variant: 'balanced', jobs: new Map(), risk: false, short: [] },
    projected: 0,
    scores: [],
  };
  if (state.phaseCursor !== null || (state.outcome !== null && !state.sandbox))
    return { decision, choice: empty };

  const sc = stewardCtx(state, cfg);
  const builds = planBuilds(sc);
  decision.builds = builds.commands;
  const toDecision = (plan: Plan): Decision => {
    const d: Decision = { ...decision, assignments: {} };
    for (const p of sc.people) {
      const job = plan.jobs.get(p.id);
      if (job !== undefined && job !== p.job) d.assignments[p.id] = job;
    }
    d.apprenticeships = apprenticeships(sc, plan.jobs);
    return d;
  };
  const choice = choosePlan(sc, builds.needs, toDecision, opts.maxCandidates);
  return { decision: toDecision(choice.plan), choice };
}

export function steward(
  state: GameState,
  cfg: Config = config,
  opts: StewardOptions = {},
): Decision {
  return stewardExplained(state, cfg, opts).decision;
}
